'use client';

import { useRef } from 'react';
import { ExperienceConfiguration, NinetailedProvider } from '@ninetailed/experience.js-react';
import { NinetailedPreviewPlugin } from '@ninetailed/experience.js-plugin-preview';
import { NinetailedInsightsPlugin } from '@ninetailed/experience.js-plugin-insights';
import { NinetailedPrivacyPlugin } from '@ninetailed/experience.js-plugin-privacy';
import NinetailedSsrPlugin from '@ninetailed/experience.js-plugin-ssr';
import { NinetailedConsentSync } from 'components/NinetailedConsentSync/NinetailedConsentSync';
import { NinetailedMode } from 'types/constants';

type AudienceConfiguration = {
  id: string;
  name: string;
  description?: string;
};

type PluginType = NinetailedInsightsPlugin | NinetailedSsrPlugin | NinetailedPreviewPlugin | NinetailedPrivacyPlugin;

/**
 * Wrapper component for the NinetailedProvider
 * @param children - The child components to be wrapped
 * @param experiences - All Ninetailed experiences from Contentful
 * @param audiences - All Ninetailed audiences from Contentful
 * @param mode - The Ninetailed mode ('client' or 'server')
 * @returns The wrapped component
 */
export function NinetailedProviderWrapper({
  children,
  experiences,
  audiences,
  mode,
}: {
  children: React.ReactNode;
  experiences: ExperienceConfiguration[];
  audiences: AudienceConfiguration[];
  mode: NinetailedMode;
}) {
  // Use ref to ensure plugins are only created ONCE per component lifetime
  const pluginsRef = useRef<PluginType[] | null>(null);

  if (pluginsRef.current === null) {
    const pluginList: PluginType[] = [
      // Insights Plugin to track experiences on the Insights tab
      new NinetailedInsightsPlugin(),
      // SSR Plugin to direct the Insight plugin to look in cookies, not in localStorage, for the correct ID
      new NinetailedSsrPlugin(),
      // Privacy Plugin defaults to consent-not-given until window.ninetailed.consent(true) is called.
      // NinetailedConsentSync wires that call to the OneTrust dispatcher (gated on C0002 Performance).
      new NinetailedPrivacyPlugin(),
    ];

    // Only add the Preview Plugin in when in client mode
    // This is because the Preview Plugin makes everything slower since it requires client-side rendering
    // QA will return client mode by default
    if (mode === 'client') {
      pluginList.push(
        new NinetailedPreviewPlugin({
          experiences: experiences || [],
          audiences: audiences || [],
        }),
      );
    }

    pluginsRef.current = pluginList;
  }

  return (
    <NinetailedProvider
      clientId={process.env.NEXT_PUBLIC_NINETAILED_API_KEY || ''}
      environment="main"
      plugins={pluginsRef.current}>
      <NinetailedConsentSync />
      {children}
    </NinetailedProvider>
  );
}
